import { useEffect, useState, useCallback } from "react";
import { Zap, RefreshCw, Loader2, Bot, Hand } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getGateTrades, fmtPrice } from "@/lib/api";

const REASON_TR = {
  open: "Alım", dca_buy: "Kademeli Alım", take_profit: "Kâr Al",
  stop_loss: "Stop-Loss", signal_close: "Sinyal Kapanış", bot_stop: "Bot Durdurma",
  manual: "Manuel",
  grid_buy: "Grid Alım", grid_sell: "Grid Satış",
};

const fmtNum = (v, d = 2) => {
  const n = Number(v);
  if (!isFinite(n) || n === 0) return "—";
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};

export const LiveTrades = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await getGateTrades()); } catch { setRows([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-3xl space-y-4" data-testid="live-trades">
      <div className="flex items-center gap-2">
        <Zap className="h-5 w-5 text-[var(--warn)]" />
        <h2 className="font-head font-bold text-xl">Canlı İşlemler · Gate.io</h2>
        <Button data-testid="refresh-live-trades" size="sm" variant="outline" onClick={load} disabled={loading}
          className="ml-auto h-9 border-[var(--border)] hover:bg-[var(--surface-hover)]">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1.5" /> Yenile</>}
        </Button>
      </div>
      <p className="text-xs text-[var(--text-faint)] leading-relaxed">
        Gate.io hesabınızda gerçekleşen gerçek spot emirleri (manuel + bot). Kağıt (paper) simülasyon işlemleri burada görünmez.
      </p>

      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        {loading ? (
          <p className="text-center py-10 text-[var(--text-faint)] text-sm animate-pulse">Yükleniyor...</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-10 text-[var(--text-faint)] text-sm" data-testid="no-live-trades">
            Henüz gerçek Gate.io işlemi yok. Gate.io bağlayıp manuel bir işlem yapın veya botu "Gerçek" modda başlatın.
          </p>
        ) : (
          <>
            {/* desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] border-b border-[var(--border)]">
                    <th className="text-left font-medium px-5 py-3">Tarih</th>
                    <th className="text-left font-medium px-3 py-3">Parite</th>
                    <th className="text-left font-medium px-3 py-3">Yön</th>
                    <th className="text-right font-medium px-3 py-3">Fiyat</th>
                    <th className="text-right font-medium px-3 py-3">Miktar</th>
                    <th className="text-right font-medium px-3 py-3">Değer (USDT)</th>
                    <th className="text-left font-medium px-3 py-3">Neden</th>
                    <th className="text-right font-medium px-5 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.gate_order_id || r.created_at} className="border-b border-[var(--surface-hover)] hover:bg-[var(--surface-hover)]">
                      <td className="px-5 py-3 text-xs text-[var(--text-dim)] font-mono">
                        {new Date(r.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-3 py-3 font-head font-bold">{r.pair}</td>
                      <td className={cn("px-3 py-3 font-bold", r.side === "buy" ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
                        {r.side === "buy" ? "ALIM" : "SATIM"}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-[var(--text-primary)]" data-testid={`live-trade-price-${r.gate_order_id}`}>
                        {fmtPrice(r.avg_deal_price ?? r.price)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-[var(--text-dim)]">{r.amount}</td>
                      <td className="px-3 py-3 text-right font-mono text-[var(--text-primary)]">{fmtNum(r.filled_total)}</td>
                      <td className="px-3 py-3 text-xs text-[var(--text-dim)]">{REASON_TR[r.reason] || "—"}</td>
                      <td className="px-5 py-3 text-right"><StatusBadge s={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* mobile cards */}
            <div className="md:hidden divide-y divide-[var(--border)]">
              {rows.map((r) => (
                <div key={r.gate_order_id || r.created_at} className="p-4" data-testid={`live-trade-card-${r.gate_order_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded",
                        r.side === "buy" ? "bg-[#00FFA3]/10 text-[var(--pos)]" : "bg-[#FF3366]/10 text-[var(--neg)]")}>
                        {r.side === "buy" ? "ALIM" : "SATIM"}
                      </span>
                      <span className="font-head font-bold text-sm">{r.pair}</span>
                    </div>
                    <span className="text-[11px] text-[var(--text-faint)] font-mono">
                      {new Date(r.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs font-mono">
                    <div className="flex justify-between"><span className="text-[var(--text-faint)]">Fiyat</span><span data-testid={`live-trade-price-mobile-${r.gate_order_id}`} className="text-[var(--text-primary)]">{fmtPrice(r.avg_deal_price ?? r.price)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-faint)]">Değer</span><span className="text-[var(--text-primary)]">{fmtNum(r.filled_total)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-faint)]">Miktar</span><span className="text-[var(--text-dim)]">{r.amount}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-faint)]">Kaynak</span><span>{r.bot ? "Bot" : "Manuel"}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-faint)]">Neden</span><span>{REASON_TR[r.reason] || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-faint)]">Durum</span><StatusBadge s={r.status} /></div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ s }) => {
  const done = s === "closed" || s === "finished";
  return (
    <span className={cn("text-xs font-mono", done ? "text-[var(--pos)]" : "text-[var(--warn)]")}>
      {s === "closed" ? "tamamlandı" : s === "open" ? "açık" : (s || "—")}
    </span>
  );
};
